import get from 'lodash/get.js';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle.tsx';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs.tsx';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults.jsx';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem.jsx';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader.jsx';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast.jsx';
import { SbRespMainContent } from '../SbRespMainContent/SbRespMainContent.tsx';
import { SbSharedContentModuleIconicDesign } from '../SbSharedContentModuleIconicDesign/SbSharedContentModuleIconicDesign.jsx';
import { SbRespCreatorDesigns } from '../SbSharedCreatorDesigns/SbRespCreatorDesigns.tsx';
import { SbSharedSiblingDesigns } from '../SbSharedSiblingDesigns/SbSharedSiblingDesigns.jsx';
import { SbSharedEmptyDesignsBannerWrapper } from '../SbSharedEmptyDesigns/SbSharedEmptyDesignsBannerWrapper.jsx';
import { SharedIconicDesignHeadingsWrapper } from '../../shared/SharedIconicDesignHeadingsWrapper/SharedIconicDesignHeadingsWrapper.tsx';
import { SharedWordpressArticles } from '../../shared/SharedWordpressArticles/SharedWordpressArticles.jsx';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';
import { useCurrency } from '../../shared/hooks/useCurrency.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

// constants
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants.ts';
import { ICONIC_DESIGN_MODULE_TYPE } from '../../constants/contentModuleConstants.js';

// styles
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbRespDesignComponents.scss';

function getSearchTerm(filters) {
    return get(
        filters.filter(({ name }) => name === FILTER_REFINE_SEARCH),
        '[0].values[0].urlLabel'
    );
}

function SbRespDesignsComponentsComponent(props) {
    const {
        viewer,
        itemSearch,
        user,
        generatedUriRef,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
    } = props;

    const { currency } = useCurrency();
    const { isQuickFiltersRedesignVariant, isDriveSavedSearchAdoptionVariant } =
        useServerVarsContext();
    const { totalResults, appliedFilters } = itemSearch;

    const { isWordpressArticlesEnabled } = viewer;

    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT;
    const searchTerm = getSearchTerm(appliedFilters);

    const designPageInfo = itemSearch?.designPageInfo;
    const browseContentModule = itemSearch?.browseContentModule;
    const showContentModule =
        browseContentModule?.moduleType === ICONIC_DESIGN_MODULE_TYPE &&
        browseContentModule?.title &&
        browseContentModule?.mainImage;

    const isEmptyDesignsPage = !totalResults && showContentModule;
    const isNullResultsPage = !totalResults && !showContentModule;

    return (
        <SharedSaveSearchContextProvider
            itemSearch={itemSearch}
            user={user}
            searchTerm={searchTerm}
            uriRef={generatedUriRef}
        >
            {isNullResultsPage ? (
                <SbRespNullSearchResults
                    viewer={viewer}
                    itemSearch={itemSearch}
                    user={user}
                    isClient={isClient}
                    searchTerm={searchTerm}
                />
            ) : (
                <>
                    {showContentModule ? (
                        <div
                            className={classnames(
                                styles.pageTopBackground,
                                dibsCss.relative,
                                dibsCss.wScreen,
                                dibsCss.mbSmall,
                                dibsCss.bgMoonstone
                            )}
                        >
                            <div
                                className={classnames(
                                    styles.pageTopContent,
                                    isQuickFiltersRedesignVariant
                                        ? styles.maxW1640px
                                        : dibsCss.maxW1440px,
                                    dibsCss.pbLarger,
                                    dibsCss.mxAuto
                                )}
                            >
                                <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
                                <SbSharedContentModuleIconicDesign
                                    browseContentModule={browseContentModule}
                                    designPageInfo={designPageInfo}
                                />
                            </div>
                        </div>
                    ) : (
                        <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
                    )}
                    <SharedIconicDesignHeadingsWrapper
                        itemSearch={itemSearch}
                        showContentModule={showContentModule}
                    >
                        {Heading => (
                            <>
                                {!isEmptyDesignsPage ? (
                                    <>
                                        {/* Page Header */}
                                        <div
                                            className={classnames(
                                                dibsCss.flex,
                                                dibsCss.justifyBetween
                                            )}
                                        >
                                            <div
                                                className={classnames(
                                                    dibsCss.flex,
                                                    dibsCss.flexCol,
                                                    dibsCss.wFull
                                                )}
                                            >
                                                <div
                                                    className={classnames(
                                                        dibsCss.flex,
                                                        isDriveSavedSearchAdoptionVariant
                                                            ? [
                                                                  dibsCss.itemsBaseline,
                                                                  dibsCss.gapXsmall,
                                                              ]
                                                            : [
                                                                  dibsCss.itemsCenter,
                                                                  dibsCss.justifyBetween,
                                                              ]
                                                    )}
                                                >
                                                    <SbSharedTitle
                                                        itemSearch={itemSearch}
                                                        user={user}
                                                        Heading={Heading}
                                                    />
                                                    <SharedFollowTopButton
                                                        itemSearch={itemSearch}
                                                        user={user}
                                                    />
                                                </div>
                                            </div>
                                            {relayVariables.isTrade && isClient && (
                                                <div
                                                    className={classnames(
                                                        dibsCss.grow,
                                                        dibsCss.textRight
                                                    )}
                                                >
                                                    <TradePromoLoader
                                                        showIcon
                                                        user={user}
                                                        viewer={viewer}
                                                        displayInfo={null}
                                                        item={null}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <SbRespMainContent
                                            currency={currency}
                                            itemSearch={itemSearch}
                                            viewer={viewer}
                                            user={user}
                                            isClient={isClient}
                                            isThinPage={isThinPage}
                                            isThinBrowsePage={false}
                                            relay={relay}
                                        />
                                    </>
                                ) : (
                                    <SbSharedEmptyDesignsBannerWrapper
                                        itemSearch={itemSearch}
                                        isClient={isClient}
                                    />
                                )}
                                {/* Series Sibling Designs*/}
                                <SbSharedSiblingDesigns designPageInfo={designPageInfo} />
                                {/*  Creator Designs */}
                                <SbRespCreatorDesigns
                                    designPageInfo={designPageInfo}
                                    currency={currency}
                                />
                                {/* Related Wordpress Articles */}
                                {isWordpressArticlesEnabled && (
                                    <SharedWordpressArticles
                                        itemSearch={itemSearch}
                                        launchOrigin={PAGE_TYPE.DESIGN}
                                    />
                                )}
                            </>
                        )}
                    </SharedIconicDesignHeadingsWrapper>
                    {/* Unavailable Item Modal */}
                    <SbRespNotificationUnavailableItem />
                    {/* Personalized Filter Toast */}
                    <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
                </>
            )}
        </SharedSaveSearchContextProvider>
    );
}

SbRespDesignsComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
};

const mapStateToProps = state => {
    const { filters, relayVariables } = state;
    const { generatedUriRef, refetchInFlight } = filters;

    return {
        refetchInFlight,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespDesignsComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespDesignsComponentsComponent),
    {
        itemSearch: graphql`
            fragment SbRespDesignsComponents_itemSearch on ItemSearchQueryConnection {
                totalResults
                ...SbSharedForceSearchLink_itemSearch
                ...SbSharedSearchSuggestions_itemSearch
                ...SbRespNullSearchResults_itemSearch
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbRespCategoryTiles_itemSearch
                ...SbRespPriceTiles_itemSearch @include(if: $isTrade)
                ...SbRespTradeOnApprovalPromoBanner_itemSearch @include(if: $isTrade)
                ...SbRespMainContent_itemSearch
                ...SharedSaveSearchContext_itemSearch
                ...SbSharedEmptyDesignsBannerWrapper_itemSearch
                ...SharedIconicDesignHeadingsWrapper_itemSearch
                ...SharedWordpressArticles_itemSearch
                ...SharedFollowTopButton_itemSearch
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                browseContentModule(contentModuleId: $contentModuleId) {
                    moduleType
                    ... on ContentModuleIconicDesign {
                        mainImage
                        title
                    }
                    ...SbSharedContentModuleIconicDesign_browseContentModule
                }
                designPageInfo(maxSiblingDesigns: 4, maxCreatorDesigns: 5) {
                    ...SbSharedContentModuleIconicDesign_designPageInfo
                    ...SbSharedSiblingDesigns_designPageInfo
                    ...SbRespCreatorDesigns_designPageInfo
                }
                ...SbSharedEmptyDesignsBannerWrapper_itemSearch
            }
        `,
        user: graphql`
            fragment SbRespDesignsComponents_user on User {
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespNullSearchResults_user
                ...SbRespMainContent_user
                ...SharedFollowTopButton_user
                ...SbSharedTitle_user
                ...SbSharedPageBreadcrumbs_user
                ...SharedSaveSearchContext_user
            }
        `,
        viewer: graphql`
            fragment SbRespDesignsComponents_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                ...SbRespNullSearchResults_viewer
                ...SbRespPriceTiles_viewer @include(if: $isTrade)
                ...SbRespMainContent_viewer
                ...SbRespTradeOnApprovalPromoBanner_viewer @include(if: $isTrade)
            }
        `,
        shippingFilters: graphql`
            fragment SbRespDesignsComponents_shippingFilters on ShippingFilterType
            @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
    }
);
